import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Stack,
    Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { API_URL, apiFetch } from "../config";

const defaultCategories = {
    income: ["Services", "Product sales", "Other income"],
    expense: ["Payroll", "Software", "Travel", "Supplies", "Marketing", "Other expense"],
};

function monthNow() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const emptyForm = {
    id: null,
    kind: "expense",
    amount: "",
    category: "",
    description: "",
    occurred_on: new Date().toISOString().slice(0, 10),
    customer_id: "",
};

const Finances = () => {
    const [month, setMonth] = useState(monthNow);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ income: 0, expense: 0, net: 0 });
    const [customers, setCustomers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const load = useCallback(async () => {
        try {
            const [tRes, sRes] = await Promise.all([
                apiFetch(`${API_URL}/transactions?month=${encodeURIComponent(month)}`),
                apiFetch(`${API_URL}/transactions/summary?month=${encodeURIComponent(month)}`),
            ]);
            const tData = await tRes.json();
            const sData = await sRes.json();
            if (!tRes.ok) throw new Error(tData.error || "Failed to load transactions");
            if (!sRes.ok) throw new Error(sData.error || "Failed to load summary");
            setRows(tData);
            setSummary(sData);
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    }, [month]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        apiFetch(`${API_URL}/customers`)
            .then((r) => r.json())
            .then((d) => (Array.isArray(d) ? setCustomers(d) : setCustomers([])))
            .catch(() => setCustomers([]));
    }, []);

    const openAdd = () => {
        setForm({
            ...emptyForm,
            occurred_on: `${month}-15`,
        });
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setForm({
            id: row.id,
            kind: row.kind,
            amount: String(row.amount),
            category: row.category || "",
            description: row.description || "",
            occurred_on: row.occurred_on,
            customer_id: row.customer_id || "",
        });
        setDialogOpen(true);
    };

    const save = async () => {
        const amount = parseFloat(form.amount);
        if (!form.occurred_on || Number.isNaN(amount) || amount <= 0) {
            setSnackbar({ open: true, message: "Valid amount and date are required.", severity: "warning" });
            return;
        }
        const payload = {
            kind: form.kind,
            amount,
            category: form.category || null,
            description: form.description || null,
            occurred_on: form.occurred_on,
            customer_id: form.customer_id || null,
        };
        try {
            const url = form.id ? `${API_URL}/transactions/${form.id}` : `${API_URL}/transactions`;
            const res = await apiFetch(url, {
                method: form.id ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Save failed");
            setSnackbar({ open: true, message: "Saved.", severity: "success" });
            setDialogOpen(false);
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const remove = async (id) => {
        try {
            const res = await apiFetch(`${API_URL}/transactions/${id}`, { method: "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Delete failed");
            setSnackbar({ open: true, message: "Deleted.", severity: "success" });
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const fmt = (n) =>
        new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: "text.primary" }}>
                Finances
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track income and expenses by month. Link a customer when a transaction is tied to an account.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
                <TextField
                    label="Month"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
                    Add transaction
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip color="success" variant="filled" label={`Income: ${fmt(summary.income)}`} />
                <Chip color="error" variant="filled" label={`Expenses: ${fmt(summary.expense)}`} />
                <Chip
                    color={summary.net >= 0 ? "success" : "error"}
                    variant="filled"
                    label={`Net: ${fmt(summary.net)}`}
                />
            </Stack>

            <TableContainer component={Paper} sx={{ backgroundColor: "background.paper" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography color="text.secondary">No transactions this month.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.occurred_on}</TableCell>
                                    <TableCell sx={{ textTransform: "capitalize" }}>{row.kind}</TableCell>
                                    <TableCell>{row.category || "—"}</TableCell>
                                    <TableCell>{row.description || "—"}</TableCell>
                                    <TableCell>
                                        {customers.find((c) => c.id === row.customer_id)?.name || "—"}
                                    </TableCell>
                                    <TableCell align="right">{fmt(row.amount)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => openEdit(row)}>
                                            Edit
                                        </Button>
                                        <Button size="small" color="error" onClick={() => remove(row.id)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? "Edit transaction" : "Add transaction"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Type"
                        fullWidth
                        margin="normal"
                        select
                        SelectProps={{ native: true }}
                        value={form.kind}
                        onChange={(e) => setForm({ ...form, kind: e.target.value, category: "" })}
                    >
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                    </TextField>
                    <TextField
                        label="Amount"
                        fullWidth
                        margin="normal"
                        type="number"
                        inputProps={{ min: 0, step: "0.01" }}
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Quick category
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mb: 1, mt: 0.5 }}>
                        {(defaultCategories[form.kind] || []).map((c) => (
                            <Chip
                                key={c}
                                label={c}
                                size="small"
                                onClick={() => setForm({ ...form, category: c })}
                                variant={form.category === c ? "filled" : "outlined"}
                            />
                        ))}
                    </Stack>
                    <TextField
                        label="Category"
                        fullWidth
                        margin="normal"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        placeholder="e.g. Payroll, Services"
                    />
                    <TextField
                        label="Date"
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={form.occurred_on}
                        onChange={(e) => setForm({ ...form, occurred_on: e.target.value })}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        margin="normal"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <TextField
                        label="Customer (optional)"
                        fullWidth
                        margin="normal"
                        select
                        SelectProps={{ native: true }}
                        value={form.customer_id}
                        onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    >
                        <option value="">None</option>
                        {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Finances;
